import {match} from 'ts-pattern';
import {DeepPartial} from 'utility-types';

import {isNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {depositDefaultPaymentTypeSchema} from '../schemas/depositDefaultPaymentTypeSchema';
import {DepositFormValues} from '../schemas/depositSchema';
import {DepositResponse} from '../types/DepositResponse';

type TemplateIdResolver = (templateId: string | Nullish) => string | undefined;

// A disallowed deposit method keeps its document configuration untouched, so healing never rewrites
// a configuration the operator cannot see on the collapsed card. Only a missing or null flag is
// normalized to false, because the form schema requires a real boolean. Any other malformed value is
// left to fail validation rather than be coerced into a save that wipes the stored configuration.
const withTaxDocumentTemplateId = <
  T extends {isAllowed?: boolean | null; taxDocument?: {templateId?: string | null} | null},
>(
  payment: T,
  resolveTemplateId: TemplateIdResolver
) =>
  match(payment.isAllowed)
    .with(true, () => ({
      ...payment,
      taxDocument: {
        ...payment.taxDocument,
        templateId: resolveTemplateId(payment.taxDocument?.templateId),
      },
    }))
    .when(isNil, () => ({...payment, isAllowed: false}))
    .otherwise(() => payment);

// The deposit response already declares these three values, so this only filters a response that
// violates its own contract — reachable because response schemas run only under isDev && !isCI.
const toDepositDefaultPaymentType = (value: string | Nullish) => {
  const parsed = depositDefaultPaymentTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
};

export function buildDepositDefaultValues(
  deposit: DepositResponse | Nullish,
  resolveInvoiceTemplateId: TemplateIdResolver
): DeepPartial<DepositFormValues> {
  return {
    allowDeposit: deposit?.allowDeposit ?? false,
    // The deposit template is hidden and still submitted while deposits are switched off, so it
    // is only healed once they are allowed.
    templateId: isTrue(deposit?.allowDeposit)
      ? resolveInvoiceTemplateId(deposit?.templateId)
      : (deposit?.templateId ?? undefined),
    documentSeriesId: deposit?.documentSerieId ?? undefined,
    defaultPaymentType: toDepositDefaultPaymentType(deposit?.defaultPaymentType),
    cashPayment: withTaxDocumentTemplateId(
      deposit?.cashPayment ?? {isAllowed: false},
      resolveInvoiceTemplateId
    ),
    cardPayment: withTaxDocumentTemplateId(
      deposit?.cardPayment ?? {isAllowed: false},
      resolveInvoiceTemplateId
    ),
    bankPayment: withTaxDocumentTemplateId(
      deposit?.bankPayment ?? {isAllowed: false},
      resolveInvoiceTemplateId
    ),
  };
}
